import { p } from 'framer-motion/client';
import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

const Space404Page = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showMeteor, setShowMeteor] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    const meteorInterval = setInterval(() => {
      setShowMeteor(true);
      setTimeout(() => setShowMeteor(false), 3000);
    }, 8000);

    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(meteorInterval);
    };
  }, []);

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 35%, #0f0f23 100%)',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '12em 2em',
    },
    stars: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(2px 2px at ${mousePosition.x * 0.5}% ${mousePosition.y * 0.3}%, #fff, transparent),
        radial-gradient(1px 1px at ${mousePosition.x * 0.3}% ${mousePosition.y * 0.7}%, #4FC3F7, transparent),
        radial-gradient(1px 1px at ${mousePosition.x * 0.7}% ${mousePosition.y * 0.4}%, #81C784, transparent),
        radial-gradient(2px 2px at ${mousePosition.x * 0.2}% ${mousePosition.y * 0.9}%, #CE93D8, transparent),
        radial-gradient(1px 1px at ${mousePosition.x * 0.9}% ${mousePosition.y * 0.2}%, #FFB74D, transparent)
      `,
      backgroundRepeat: 'repeat',
      backgroundSize: '300px 200px',
      animation: 'twinkle 6s linear infinite',
      pointerEvents: 'none',
      transition: 'background 0.3s ease'
    },
    nebula: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 80%, rgba(79, 195, 247, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(129, 199, 132, 0.08) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(206, 147, 216, 0.06) 0%, transparent 50%)
      `,
      animation: 'nebulaFloat 20s ease-in-out infinite',
      pointerEvents: 'none'
    },
    meteor: {
      position: 'absolute',
      width: '4px',
      height: '4px',
      background: 'linear-gradient(45deg, #fff, #4FC3F7)',
      borderRadius: '50%',
      boxShadow: '0 0 10px #4FC3F7, 0 0 20px #4FC3F7, 0 0 30px #4FC3F7',
      top: '10%',
      right: '90%',
      animation: showMeteor ? 'meteorShower 3s linear' : 'none',
      pointerEvents: 'none',
      opacity: showMeteor ? 1 : 0
    },
    astronaut: {
      width: '150px',
      height: '150px',
      margin: '0 auto 40px',
      background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 50%, #03A9F4 100%)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '60px',
      boxShadow: '0 0 40px rgba(79, 195, 247, 0.6), inset 0 0 20px rgba(255, 255, 255, 0.2)',
      animation: 'float 6s ease-in-out infinite',
      position: 'relative',
      zIndex: 10
    },
    glowRing: {
      position: 'absolute',
      width: '200px',
      height: '200px',
      border: '2px solid rgba(79, 195, 247, 0.3)',
      borderRadius: '50%',
      animation: 'rotate 20s linear infinite',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    },
    error404: {
      fontSize: '8rem',
      fontWeight: '900',
      background: 'linear-gradient(135deg, #4FC3F7 0%, #81C784 50%, #CE93D8 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: '20px',
      letterSpacing: '-4px',
      animation: 'glow 3s ease-in-out infinite alternate',
      textShadow: '0 0 20px rgba(79, 195, 247, 0.5)'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: 'white',
      marginBottom: '20px',
      animation: 'fadeInUp 1s ease-out 0.5s both'
    },
    subtitle: {
      fontSize: '1.3rem',
      color: '#4FC3F7',
      marginBottom: '30px',
      fontWeight: '500',
      animation: 'fadeInUp 1s ease-out 0.7s both'
    },
    description: {
      fontSize: '1.1rem',
      color: 'rgba(255, 255, 255, 0.8)',
      maxWidth: '600px',
      margin: '0 auto 50px',
      lineHeight: '1.7',
      animation: 'fadeInUp 1s ease-out 0.9s both'
    },
    button: {
      background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
      color: 'white',
      border: 'none',
      padding: '16px 40px',
      borderRadius: '50px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 20px rgba(79, 195, 247, 0.4)',
      animation: 'fadeInUp 1s ease-out 1.1s both, buttonPulse 4s ease-in-out infinite',
      textDecoration: 'none',
      display: 'inline-block',
      position: 'relative',
      zIndex: 10
    },
    navigationCards: {
      display: 'flex',
      justifyContent: 'center',
      gap: '30px',
      marginTop: '60px',
      flexWrap: 'wrap',
      animation: 'fadeInUp 1s ease-out 1.3s both'
    },
    navCard: {
      background: 'rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(79, 195, 247, 0.3)',
      borderRadius: '20px',
      padding: '30px 25px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '160px',
      animation: 'cardFloat 8s ease-in-out infinite'
    },
    navCardIcon: {
      fontSize: '2.5rem',
      display: 'block',
      marginBottom: '15px'
    },
    navCardTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: 'white',
      marginBottom: '8px'
    },
    navCardDesc: {
      fontSize: '0.9rem',
      color: 'rgba(255, 255, 255, 0.7)',
      lineHeight: '1.4'
    },
    planets: {
      position: 'absolute',
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #CE93D8 0%, #AB47BC 100%)',
      top: '15%',
      left: '10%',
      animation: 'planetOrbit 30s linear infinite',
      boxShadow: '0 0 20px rgba(206, 147, 216, 0.4)',
      pointerEvents: 'none'
    },
    planet2: {
      position: 'absolute',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #81C784 0%, #4CAF50 100%)',
      bottom: '20%',
      right: '15%',
      animation: 'planetOrbit2 25s linear infinite reverse',
      boxShadow: '0 0 15px rgba(129, 199, 132, 0.4)',
      pointerEvents: 'none'
    },
    statusIndicator: {
      position: 'absolute',
      bottom: '30px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '0.9rem'
    },
    statusDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#4FC3F7',
      animation: 'pulse 2s ease-in-out infinite'
    }
  };

  const keyframes = `
    @keyframes twinkle {
      0%, 100% { opacity: 0.3; }
      50% { opacity: 1; }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      50% { transform: translateY(-20px) rotate(5deg); }
    }
    
    @keyframes rotate {
      from { transform: translate(-50%, -50%) rotate(0deg); }
      to { transform: translate(-50%, -50%) rotate(360deg); }
    }
    
    @keyframes glow {
      0% { text-shadow: 0 0 20px rgba(79, 195, 247, 0.5), 0 0 30px rgba(79, 195, 247, 0.3); }
      100% { text-shadow: 0 0 30px rgba(79, 195, 247, 0.8), 0 0 40px rgba(79, 195, 247, 0.5); }
    }
    
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes buttonPulse {
      0%, 100% { box-shadow: 0 4px 20px rgba(79, 195, 247, 0.4); }
      50% { box-shadow: 0 6px 30px rgba(79, 195, 247, 0.6); }
    }
    
    @keyframes cardFloat {
      0%, 100% { transform: translateY(0px); }
      33% { transform: translateY(-8px); }
      66% { transform: translateY(4px); }
    }
    
    @keyframes nebulaFloat {
      0%, 100% { transform: rotate(0deg) scale(1); }
      50% { transform: rotate(180deg) scale(1.1); }
    }
    
    @keyframes meteorShower {
      0% { 
        opacity: 0; 
        top: 10%; 
        right: 90%; 
        transform: scale(0);
      }
      10% { 
        opacity: 1; 
        transform: scale(1);
      }
      90% { 
        opacity: 1; 
      }
      100% { 
        opacity: 0; 
        top: 90%; 
        right: 10%; 
        transform: scale(0);
      }
    }
    
    @keyframes planetOrbit {
      0% { transform: rotate(0deg) translateX(100px) rotate(0deg); }
      100% { transform: rotate(360deg) translateX(100px) rotate(-360deg); }
    }
    
    @keyframes planetOrbit2 {
      0% { transform: rotate(0deg) translateX(80px) rotate(0deg); }
      100% { transform: rotate(360deg) translateX(80px) rotate(-360deg); }
    }
    
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(1.2); }
    }
    
    @media (max-width: 768px) {
      .error404 { font-size: 5rem; }
      .title { font-size: 2rem; }
      .navigationCards { flex-direction: column; align-items: center; }
      .navCard { min-width: 200px; }
    }
  `;

  return (
    <div>
    <Navbar />  
    <div style={styles.container}>
      <style>{keyframes}</style>
      
      {/* Space Environment */}
      <div style={styles.stars}></div>
      <div style={styles.nebula}></div>
      <div style={styles.meteor}></div>
      <div style={styles.planets}></div>
      <div style={styles.planet2}></div>
      
      {/* Main Content */}
      <div style={{position: 'relative', zIndex: 5}}>
        <div style={styles.astronaut}>
          <div style={styles.glowRing}></div>
          🧠
        </div>
        
        <div style={styles.error404}>404</div>
        
        <h1 style={styles.title}>Learning Path Not Found</h1>
        <p style={styles.subtitle}>AI System Alert: Navigation Error</p>
        <p style={styles.description}>
          The educational module you're looking for has drifted into the digital cosmos. Our neural network navigation systems are recalibrating to guide you back to the Global AI Society learning hub. Even the most advanced AI algorithms sometimes need a course correction!
        </p>
        
        <button 
          style={styles.button}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = '0 6px 30px rgba(79, 195, 247, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(79, 195, 247, 0.4)';
          }}
        >
          🧠 Return to Learning Hub
        </button>
        
        <div style={styles.navigationCards}>
          <div 
            style={{...styles.navCard, animationDelay: '0s'}}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
              e.currentTarget.style.borderColor = 'rgba(79, 195, 247, 0.6)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(79, 195, 247, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.borderColor = 'rgba(79, 195, 247, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span style={styles.navCardIcon}>📚</span>
            <div style={styles.navCardTitle}>Browse Courses</div>
            <p style={styles.navCardDesc}>Explore AI learning modules</p>
          </div>
          
          <div 
            style={{...styles.navCard, animationDelay: '2s'}}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
              e.currentTarget.style.borderColor = 'rgba(129, 199, 132, 0.6)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(129, 199, 132, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.borderColor = 'rgba(79, 195, 247, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span style={styles.navCardIcon}>🤖</span>
            <div style={styles.navCardTitle}>AI Tutor</div>
            <p style={styles.navCardDesc}>Get personalized assistance</p>
          </div>
          
          <div 
            style={{...styles.navCard, animationDelay: '4s'}}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px) scale(1.05)';
              e.currentTarget.style.borderColor = 'rgba(206, 147, 216, 0.6)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(206, 147, 216, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.borderColor = 'rgba(79, 195, 247, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span style={styles.navCardIcon}>🔍</span>
            <div style={styles.navCardTitle}>Learning Paths</div>
            <p style={styles.navCardDesc}>Discover structured journeys</p>
          </div>
        </div>
      </div>
      
      {/* Status Indicator */}
      <div style={styles.statusIndicator}>
        <div style={styles.statusDot}></div>
        <span>Neural Network Status: Active</span>
      </div>
    </div>
    <Footer />
    </div>
  );
};

export default Space404Page;